// Types + pure helpers + fetchers for the onboarding-funnel Board (kanban) view.
// Mirrors the dashboard-api `GET /funnel/board` response.

import { dashboardRequest } from "../providers/aws/client";
import type { Contact } from "../types";
import { toCohortWeeks, type DateRange } from "../filters/dateRange";

export type FunnelBoardContact = Contact & {
  funnel_stage?: string | null;
  funnel_outcome?: string | null;
  funnel_recovered?: boolean | null;
  funnel_cohort_date?: string | null;
  is_recurring?: boolean | null;
  /** When the contact entered its current furthest stage (for "stuck Nd"). */
  stage_entered_at?: string | null;
};

export type FunnelBoardColumn = {
  key: string;
  label: string;
  total: number;
  has_more: boolean;
  contacts: FunnelBoardContact[];
};

export type FunnelBoardParams = {
  cohort_from: string | null;
  cohort_to: string | null;
  leg: string | null;
  skill: string;
  acquisition_group?: string | null;
};

export type FunnelBoard = {
  params: FunnelBoardParams;
  incomplete_cohort: boolean;
  cohort_size: number;
  page_size: number;
  columns: FunnelBoardColumn[];
  recurring: FunnelBoardColumn;
  truncated?: boolean;
};

export const FUNNEL_BOARD_RECURRING_KEY = "recurring";

// Stage labels mirror dashboard-api FUNNEL_STAGE_LABELS (board columns are
// server-driven, but the modal needs a label from a bare stage key).
export const FUNNEL_STAGE_LABELS: Record<string, string> = {
  // Pre-contact snap stage, prepended before "Conversation started" only when
  // Source=Snap. Sourced from the bot's token-keyed funnel, not the dashboard.
  qr_scanned: "QR scanned",
  // Pre-contact launchpad stages, prepended before "Conversation started" only
  // when Source=Launchpad. Sourced from the bot's token-keyed funnel, not the
  // dashboard.
  session_started: "Launchpad started",
  media_ready: "Uploads finalized",
  conversation_started: "Conversation started",
  privacy_acknowledged: "Privacy acknowledged",
  marketing_opt_in_answered: "Marketing opt-in",
  create_invoked: "/create invoked",
  occasion_selected: "Occasion selected",
  images_uploaded: "Images uploaded",
  motif_entered: "Echo motif",
  interview: "Interview",
  echo_delivered: "Echo delivered",
  rated: "Rated",
  [FUNNEL_BOARD_RECURRING_KEY]: "Recurring",
};

export function stageLabel(stage?: string | null): string {
  if (!stage) return "—";
  return FUNNEL_STAGE_LABELS[stage] ?? humanizeStageKey(stage);
}

/** A stage the CRM has no label for yet still reads as words, not a raw key. */
function humanizeStageKey(stage: string): string {
  const words = stage.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : stage;
}

export const FUNNEL_BOARD_CHANNEL_LABELS: Record<string, string> = {
  website: "Website",
  ctwa: "Meta → WhatsApp",
  organic: "Organic",
  snap: "Snap",
  link: "Tracking link",
  launchpad: "Launchpad",
};

export function channelLabel(channel?: string | null): string {
  if (!channel) return "Organic";
  return FUNNEL_BOARD_CHANNEL_LABELS[channel] ?? channel;
}

/** True when a failed Echo subsequently reached accepted channel delivery. */
export function isRecoveredFunnelContact(contact: FunnelBoardContact): boolean {
  return (
    contact.funnel_recovered === true ||
    contact.funnel_outcome === "recovered" ||
    (contact.funnel_outcome === "error" &&
      (contact.has_completed_video === true ||
        (contact.completed_echo_count ?? 0) > 0))
  );
}

/** Current unresolved error, excluding contacts whose delivery recovered. */
export function hasActiveFunnelError(contact: FunnelBoardContact): boolean {
  return (
    contact.funnel_outcome === "error" && !isRecoveredFunnelContact(contact)
  );
}

/** Whole days since the contact entered its current stage; null if unknown. */
export function stuckDays(
  stageEnteredAt?: string | null,
  now: Date = new Date(),
): number | null {
  if (!stageEnteredAt) return null;
  const entered = new Date(stageEnteredAt).getTime();
  if (!Number.isFinite(entered)) return null;
  const diffMs = now.getTime() - entered;
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / 86_400_000);
}

/** A human label for a card/modal — real name, else the WhatsApp number. */
export function contactDisplayName(contact: FunnelBoardContact): string {
  const name = [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name && name.toLowerCase() !== "whatsapp") return name;
  return contact.whatsapp_user_id || String(contact.id);
}

export function boardQuery(opts: {
  range: DateRange;
  leg: string;
  acquisitionGroup?: string;
  limit?: number;
  stage?: string;
  offset?: number;
}): string {
  // The board reads the same weekly cohort window as the summary, so a custom
  // range sends `cohort_from`/`cohort_to` (which override `range` server-side).
  const params = new URLSearchParams(
    toCohortWeeks(opts.range) ?? {
      range: (opts.range as { preset: string }).preset,
    },
  );
  if (opts.leg && opts.leg !== "all") params.set("leg", opts.leg);
  if (opts.acquisitionGroup && opts.acquisitionGroup !== "all")
    params.set("acquisition_group", opts.acquisitionGroup);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.offset != null) params.set("offset", String(opts.offset));
  return params.toString();
}

export function fetchFunnelBoard(opts: {
  range: DateRange;
  leg: string;
  acquisitionGroup?: string;
  limit?: number;
}): Promise<FunnelBoard> {
  return dashboardRequest<{ data: FunnelBoard }>(
    `/funnel/board?${boardQuery(opts)}`,
  ).then((res) => res.data);
}

export function fetchFunnelBoardStage(opts: {
  range: DateRange;
  leg: string;
  acquisitionGroup?: string;
  stage: string;
  offset: number;
  limit?: number;
}): Promise<FunnelBoardColumn> {
  return dashboardRequest<{ data: FunnelBoardColumn }>(
    `/funnel/board?${boardQuery(opts)}`,
  ).then((res) => res.data);
}
